import { useEffect, useState, useCallback } from "react";
import MapConfig from "../utils/map-config.json";
import { getDatasetsJson } from "../utils/DatasetsJson";

const LegendControl = ({ layers }) => {
  const DatasetsJson = getDatasetsJson();
  const [legends, setLegends] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getGraphicLegend = useCallback((layerNum) => {
    for (const provider of DatasetsJson) {
      for (const dataset of provider.datasets || []) {
        const found = dataset.layers?.find(layer => layer.num_layer === layerNum);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }, [DatasetsJson]); 

  useEffect(() => {
    const fetchLegends = async () => {
      try {
        setLoading(true);
        setError(null);
        const visibleLayers = layers.filter(l => l.visibility !== false).reverse();
        const newLegends = [];

        const legendPromises = visibleLayers.map(async (l) => {
          try {
            if (l.layer?._isUnesco) {
              const unescoLegend = getUnescoLegend();
              if (unescoLegend) {
                newLegends.push({
                  title: l.title,
                  unit: "",
                  items: unescoLegend
                });
              }
              return;
            }

            const isWmts = l.layer._layerType?.includes("WMTS");
            const isWms = l.layer._layerType?.includes("WMS");
            const isWfs = l.layer._layerType?.includes("WFS");

            if (isWms || isWmts) {
              let graphicLegend = getGraphicLegend(l.id);

              if (graphicLegend && graphicLegend.GetLegendGraphic) {
                newLegends.push({
                  title: l.title,
                  unit: graphicLegend.unit,
                  items: [{ label: l.title, image: graphicLegend.GetLegendGraphic }]
                });
                return;
              } else if (!graphicLegend) {
                if (isWms) {
                  try {
                    const baseUrl = l.url.split('?')[0];
                    const getLegendGraphicUrl = `${baseUrl}?service=WMS&request=GetLegendGraphic&format=image/png&version=1.0.0&layer=${l.name}`;

                    newLegends.push({
                      title: l.title,
                      unit: graphicLegend.unit,
                      items: [{ label: l.title, image: getLegendGraphicUrl }]
                    });
                    return;
                  } catch (e) {
                    console.error(`Error loading WMS legend for ${l.title}:`, e);
                  }
                } else if (isWmts) {
                  console.warn(`No legend found for WMTS layer: ${l.title}. WMTS does not support GetLegendGraphic.`);
                  newLegends.push({
                    title: l.title,
                    unit: "",
                    items: []
                  });
                  return;
                }
              } else if (isWmts && isWmts && graphicLegend?.style?.colormap) {
                let cmap = graphicLegend?.style?.colormap.split(",")[0];
                const gradient = MapConfig.Colormaps[cmap];
                newLegends.push({
                  title: l.title,
                  unit: graphicLegend.unit,
                  items: gradient ? [{ gradient: gradient, minVal: graphicLegend.valueMin, maxVal: graphicLegend.valueMax }] : []
                });
              }
            } else if (isWfs) {
              let geometryType = l.layer._geometryType;

              if (!geometryType) {
                const keys = Object.keys(l.layer._layers);

                if (keys.length > 0) {
                  const firstLayer = l.layer._layers[keys[0]];
                  const geom = firstLayer.feature?.geometry;

                  geometryType = geom?.type || "Polygon";
                } else {
                  geometryType = "Polygon";
                }
              }

              let item = null;

              if (geometryType === "LineString" || geometryType === "MultiLineString") {
                item = {
                  symbol: {
                    type: "line",
                    outline: {
                      color: "#615C9F",
                      width: 3
                    }
                  }
                };
              } else if (geometryType === "Polygon" || geometryType === "MultiPolygon") {
                item = {
                  symbol: {
                    type: "polygon",
                    color: "#B8B3D9",
                    outline: {
                      color: "#615C9F",
                      width: 2
                    }
                  }
                };
              } else if (geometryType === "Point" || geometryType === "MultiPoint") {
                item = {
                  symbol: {
                    type: "point",
                    size: 4,
                    color: "#615C9F",
                    outline: {
                      color: "#000",
                      width: 1
                    }
                  }
                };
              }

              if (item) {
                newLegends.push({
                  title: l.title || l.layer._name || "Layer",
                  items: [item]
                });
              }

              return;
            }
            
            const baseUrl = l.url.replace(/\/$/, "");
            const isMapServer = /\/MapServer$/.test(baseUrl);
            const metadata = await (await fetch(baseUrl + "?f=json")).json();

            if (isMapServer) {
              const legendRes = await fetch(baseUrl + "/legend?f=json");
              const legendJson = await legendRes.json();

              if (legendJson.layers?.length) {
                const items = [];
                for (const sub of legendJson.layers) {
                  for (const legendItem of sub.legend) {
                    if (!legendItem.imageData) continue;
                    const imageUrl = `data:${legendItem.contentType};base64,${legendItem.imageData}`;
                    items.push({
                      label: legendItem.label || sub.layerName,
                      image: imageUrl
                    });
                  }
                }
                if (items.length) {
                  newLegends.push({
                    title: l.title,
                    unit: getGraphicLegend(l.id).unit,
                    items
                  });
                  return;
                }
              }

              if (metadata.layers?.length) {
                for (const sub of metadata.layers) {
                  const subUrl = baseUrl + "/" + sub.id;
                  const subMeta = await (await fetch(subUrl + "?f=json")).json();
                  if (subMeta.drawingInfo?.renderer) {
                    const items = extractItemsFromRenderer(subMeta.drawingInfo.renderer);
                    if (items.length) {
                      newLegends.push({
                        title: `${l.title} - ${sub.name}`,
                        unit: getGraphicLegend(l.id).unit,
                        items
                      });
                    }
                  }
                }
              }
            } else if (metadata.drawingInfo?.renderer) {
              const items = extractItemsFromRenderer(metadata.drawingInfo.renderer);
              if (items.length) {
                newLegends.push({
                  title: l.title,
                  unit: getGraphicLegend(l.id).unit,
                  items
                });
              }
            } else {
              console.warn("Layer not found for " + l.title);
            }
          } catch (e) {
            console.error("Error loading layer for " + l.title, e);
          }
        });

        await Promise.all(legendPromises);

        setLegends(
          visibleLayers.map(l => {
            const legend = newLegends.find(legend => legend.title === l.title);
            return {
              title: l.title,
              unit: legend?.unit || "",
              items: legend?.items || []
            };
          })
        );
      } catch (e) {
        setError("Error loading legend");
        console.error("Error loading legends:", e);
        setLegends([]);
      } finally {
        setLoading(false);
      }
    };

    if (layers.length) {
      fetchLegends();
    } else {
      setLegends([]);
    }
  }, [layers, getGraphicLegend]);

  const extractItemsFromRenderer = (renderer) => {
    const items = [];

    const pushItem = (label, symbol) => {
      items.push({ label, symbol });
    };

    if (renderer.symbol) {
      pushItem(renderer.label || "Symbol", renderer.symbol);
    }

    if (renderer.uniqueValueInfos) {
      renderer.uniqueValueInfos.forEach(info =>
        pushItem(info.label, info.symbol)
      );
    }

    if (renderer.classBreakInfos) {
      renderer.classBreakInfos.forEach(info =>
        pushItem(info.label, info.symbol)
      );
    }

    return items;
  };

  const getUnescoLegend = () => {
    const unescoStyle = MapConfig.LayerStyles.find(a => a.name === "UNESCO")?.style;

    if (!Array.isArray(unescoStyle)) return null;

    const items = unescoStyle.map((styleItem) => ({
      label: styleItem.label || "Not in danger",
      symbol: {
        type: "point",
        size: 4,
        color: styleItem.color,
        outline: {
          color: [0, 0, 0, 255],
          width: 1
        }
      }
    }));

    return items;
  };

  const renderSymbol = (symbol) => {
    if (!symbol || !symbol.type) return null;
    const color = (value) => {
      if (!value) return "rgba(0,0,0,1)";
      if (Array.isArray(value)) {
        const [r, g, b, a = 255] = value;
        return `rgba(${r},${g},${b},${a / 255})`;
      }
      return value;
    };

    const strokeColor = symbol.outline ? color(symbol.outline.color) : "#000";
    const strokeWidth = symbol.outline?.width || 1;
    const fillColor = color(symbol.color);

    switch (symbol.type) {
      case "line":
        return (
          <svg width="20" height="20">
            <line
              x1="0"
              y1="10"
              x2="20"
              y2="10"
              stroke={strokeColor}
              strokeWidth={strokeWidth}
            />
          </svg>
        );
      case "polygon":
        return (
          <svg width="20" height="20">
            <rect
              x="2"
              y="2"
              width="16"
              height="16"
              fill={fillColor}
              stroke={strokeColor}
              strokeWidth={strokeWidth}
            />
          </svg>
        );
      case "point":
        return (
          <svg width="20" height="20">
            <circle
              cx="10"
              cy="10"
              r={symbol.size || 4}
              fill={fillColor}
              stroke={strokeColor}
              strokeWidth={strokeWidth}
            />
          </svg>
        );
      default:
        return <div>Symbol Type: {symbol.type}</div>;
    }
  };

  const computeNiceStep = (min, max, target = 6) => {
    const span = Math.max(max - min, 0);
    const step0 = span / Math.max(1, target);
    const pow = Math.floor(Math.log10(step0));
    const base = Math.pow(10, pow);
    const err = step0 / base;
    if (err >= Math.sqrt(50)) return 10 * base;
    if (err >= Math.sqrt(10)) return 5 * base;
    if (err >= Math.sqrt(2))  return 2 * base;
    return base;
  }

  const buildTicks = (min, max, target = 6) => {
    const step = computeNiceStep(min, max, target - 1);
    const lastTick = Math.floor(max / step) * step;
    const ticks = [];
    for (let v = min; v <= lastTick + 1e-9; v += step) {
      ticks.push(Number(v.toFixed(2)));
    }
    return ticks;
  }

  const formatTick = (n, minVal, maxVal) => {
    const span = maxVal - minVal;
    if (Math.abs(n) >= 10 && span > 1) return Math.round(n).toString();
    if (Math.abs(n) < 1) return n.toFixed(2);
    return n.toFixed(1);
  }

  const GradientLegend = ({ minVal, maxVal, gradient }) => {
    const dataMin = Number(minVal);
    const dataMax = Number(maxVal);

    const ticks = buildTicks(dataMin, dataMax, 6);

    return (
      <div className="legend-qgis" style={{ position: "relative", width: "100%" }}>
        <div
          className="legend-bar"
          style={{
            background: gradient,
            height: "20px",
            position: "relative",
            width: "100%"
          }}
        />
        <div style={{ position: "relative", height: "30px" }}>
          {ticks.map((value, i) => {
          const frac = (value - dataMin) / (dataMax - dataMin);
          let leftPercent = frac * 100;

          if (i === 0) {
            leftPercent = 2;
          }

          const label = formatTick(value, dataMin, dataMax);

          return (
            <div
              key={`${value}-${i}`}
              style={{
                position: "absolute",
                left: `${leftPercent}%`,
                textAlign: "center",
                fontSize: "12px",
                whiteSpace: "nowrap",
                transform: "translateX(-50%)"
              }}
            >
              <div style={{ width: "1px", height: "0.5rem", margin: "0 auto", borderLeft: "1px solid #000" }} />
              <div>{label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

  return (
    <div className="legend-container">
      {loading && <p>Loading legends...</p>}
      {error && <div className="error-message">{error}</div>}
      {!loading && legends.length === 0 && !error && <p>No visible layers</p>}
      {!loading && legends.map((lg, i) => (
        <div key={i} className="legend-item">
          <div className="legend-title">
            {lg.title}
            {lg.unit && <span className="legend-unit">({lg.unit})</span>}
          </div>
          {lg.items.length === 0 ? (
            <div className="legend-row">
              No legend available
            </div>
          ) : (
            lg.items.map((item, j) => (
              <div key={j} className="legend-row">
                {item.image && (
                  item.image.includes("GetLegendGraphic") ? (
                    <div className="legend-scroll">
                      <img
                        src={item.image}
                        alt={item.label}
                      />
                    </div>
                  ) : (
                    <div className="legend-scroll">
                      <img
                        src={item.image}
                        alt={item.label}
                        style={{ width: 20, height: 20, marginRight: 8 }}
                      />
                      <span>{item.label}</span>
                    </div>
                  )
                )}
                {item.symbol && !item.image && 
                  <>
                    {renderSymbol(item.symbol)}
                    <span>{item.label}</span>
                  </>
                }
                {item.gradient && !item.image &&
                  <GradientLegend
                    minVal={item.minVal}
                    maxVal={item.maxVal}
                    gradient={item.gradient}
                  />
                }
              </div>
            ))
          )}
        </div>
      ))}
    </div>
  );
};

export default LegendControl;
